import type { Manifest } from "@prose-reader/shared"
import { first, map, withLatestFrom } from "rxjs"
import type { Reader } from "../../reader"
import type { SpineItem } from "../../spineItem/SpineItem"
import type { LayoutEnhancerOutput } from "../layout/layoutEnhancer"

const getTotalProgressFromPercentages = (
  estimateBeforeThisItem: number,
  currentItemWeight: number,
  progressWithinThisItem: number,
) => {
  return estimateBeforeThisItem + currentItemWeight * progressWithinThisItem
}

const getScrollPercentageWithinItem = (
  reader: Reader,
  currentPosition: { x: number; y: number },
  currentItem: SpineItem,
) => {
  const { height, width } = currentItem.layoutInfo

  const { top, left } = reader.spine.getSpineItemSpineLayoutInfo(currentItem)

  if (reader.settings.values.computedPageTurnDirection === `vertical`) {
    return Math.max(
      0,
      Math.min(
        1,
        (currentPosition.y - top + reader.viewport.absoluteViewport.height) /
          height,
      ),
    )
  }
  return Math.max(
    0,
    Math.min(
      1,
      (currentPosition.x - left + reader.viewport.absoluteViewport.width) /
        width,
    ),
  )
}

export const getPercentageEstimate = (
  reader: Reader & LayoutEnhancerOutput,
  currentSpineIndex: number,
  pageIndex: number,
  currentPosition: { x: number; y: number },
  currentItem: SpineItem,
  manifest: Manifest,
) => {
  return currentItem.isReady$.pipe(
    first(),
    withLatestFrom(reader.layoutInfo$),
    map(([itemIsReady, layout]) => {
      const isGloballyPrePaginated =
        manifest.renditionLayout === `pre-paginated`
      const readingOrderLength = manifest.spineItems.length || 0
      const estimateBeforeThisItem =
        manifest.spineItems
          .slice(0, currentSpineIndex)
          .reduce((acc, item) => acc + (item.progressionWeight ?? 0), 0) || 0
      const itemIndexNumber =
        reader.spineItemsManager.getSpineItemIndex(currentItem) ?? 0

      const numberOfSpineItems = manifest.spineItems.length ?? 0

      const spineItemNumberOfPages =
        layout.pages.filter((page) => page.itemIndex === itemIndexNumber)
          .length ?? 0

      const currentItemWeight =
        manifest.spineItems[currentSpineIndex]?.progressionWeight ??
        // if no progressionWeight is defined we "assume" the document weight to be
        // relative to the total number of documents
        (itemIndexNumber + 1) / numberOfSpineItems

      let progressWithinThisItem =
        (pageIndex + 1) * (currentItemWeight / spineItemNumberOfPages)

      if (
        !isGloballyPrePaginated &&
        currentItem.renditionLayout === `reflowable` &&
        !itemIsReady
      ) {
        progressWithinThisItem = 0
      }

      let totalProgress = estimateBeforeThisItem + progressWithinThisItem

      if (manifest.renditionFlow === `scrolled-continuous`) {
        if (itemIsReady) {
          progressWithinThisItem = getScrollPercentageWithinItem(
            reader,
            currentPosition,
            currentItem,
          )
        } else {
          // that way we avoid having a progress of 1 just because the item is not loaded and cover all screen due to smaller size.
          // Therefore it effectively prevent jump from 30% to 25% for example.
          progressWithinThisItem = 0
        }
        totalProgress = getTotalProgressFromPercentages(
          estimateBeforeThisItem,
          currentItemWeight,
          progressWithinThisItem,
        )
      }

      // because the rounding of weight use a lot of decimals we will end up with
      // something like 0.999878 for the last page
      if (
        currentSpineIndex === readingOrderLength - 1 &&
        pageIndex === spineItemNumberOfPages - 1 &&
        totalProgress > 0.99
      ) {
        return 1
      }

      return totalProgress
    }),
  )
}
